import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NzImage, NzImageService } from 'ng-zorro-antd/image';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  first,
  map,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { UploadStatus } from 'src/app/services/uploading/enums/upload-status.enum';

import { GridConfiguration } from '../../../types/grid-configuration.type';
import { UploadingListItem } from '../../../types/uploading-list-item.type';

@Component({
  selector: 'app-lot-add-photo-uploading-list',
  templateUrl: './lot-add-photo-uploading-list.component.html',
  styleUrls: ['./lot-add-photo-uploading-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotAddPhotoUploadingListComponent implements OnInit, OnDestroy {
  @Input() initialImages: string[] = [];
  @Input() noAuctionList = false;
  @Input() grid: GridConfiguration;
  @Input() itemHeightPx: number;
  @Output() images = new EventEmitter<string[]>();
  @Output() uploading = new EventEmitter<boolean>();

  uploadingListItems$: BehaviorSubject<UploadingListItem[]>;

  validItems$: Observable<UploadingListItem[]>;
  validItemsCount$: Observable<number>;

  successUploadItems$: Observable<UploadingListItem[]>;
  successItemUrls$: Observable<string[]>;
  successUploadItemsCount$: Observable<number>;

  errorUploadItems$: Observable<UploadingListItem[]>;

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly imageService: NzImageService) {}

  ngOnInit(): void {
    const initialUploadingListItems = this.initialImages.map((imageUrl) =>
      this.imageUrlToUploadingListItem(imageUrl),
    );

    this.initializeObservables(initialUploadingListItems);
    this.subscribeImageUrlChanges();
    this.subscribeUploadingStatusChanges();
  }

  private initializeObservables(initialItems: UploadingListItem[]): void {
    this.uploadingListItems$ = new BehaviorSubject<UploadingListItem[]>(initialItems);

    this.validItems$ = this.getUploadingItemsByStatus([
      UploadStatus.initial,
      UploadStatus.signing,
      UploadStatus.uploading,
      UploadStatus.success,
    ]);
    this.validItemsCount$ = this.validItems$.pipe(map((items) => items.length));

    this.successUploadItems$ = this.getUploadingItemsByStatus(UploadStatus.success);
    this.successItemUrls$ = this.successUploadItems$.pipe(
      map((items) => items.map((item) => item.url$)),
      switchMap((urls) => iif(() => !!urls.length, combineLatest(urls), of([]))),
      shareReplay(1),
    );
    this.successUploadItemsCount$ = this.successUploadItems$.pipe(map((items) => items.length));

    this.errorUploadItems$ = this.getUploadingItemsByStatus(UploadStatus.error);
  }

  trackByUploadingListItem(_: number, item: UploadingListItem): UploadingListItem {
    return item;
  }

  pushUploadingItems(items: UploadingListItem[], to: 'forward' | 'end'): void {
    if (to === 'forward') {
      this.uploadingListItems$.next([...items, ...this.currentUploadingListItems]);
    } else {
      this.uploadingListItems$.next([...this.currentUploadingListItems, ...items]);
    }
  }

  removeUploadingItem(item: UploadingListItem): void {
    const filteredItems = this.currentUploadingListItems.filter(
      (currentItem) => currentItem !== item,
    );

    this.uploadingListItems$.next(filteredItems);
  }

  async openPreview(item: UploadingListItem): Promise<void> {
    const targetIndex = await this.successUploadItems$
      .pipe(
        first(),
        map((items) => items.indexOf(item)),
      )
      .toPromise();
    const nzImages = await this.successItemUrls$
      .pipe(
        first(),
        map((urls) => urls.map((url) => <NzImage>{ src: url })),
      )
      .toPromise();

    const previewRef = this.imageService.preview(nzImages);
    previewRef.switchTo(targetIndex);
  }

  setUploadingItemAsAuctionList(item: UploadingListItem): void {
    const filteredItems = this.currentUploadingListItems.filter(
      (currentItem) => currentItem !== item,
    );

    this.uploadingListItems$.next([item, ...filteredItems]);
  }

  get currentUploadingListItems(): UploadingListItem[] {
    return this.uploadingListItems$.getValue();
  }

  private subscribeImageUrlChanges(): void {
    this.successItemUrls$
      .pipe(takeUntil(this.destroy$))
      .subscribe((imageUrls) => this.images.emit(imageUrls));
  }

  private subscribeUploadingStatusChanges(): void {
    this.getUploadingItemsByStatus([
      UploadStatus.initial,
      UploadStatus.signing,
      UploadStatus.uploading,
    ])
      .pipe(
        map((items) => !!items.length),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((uploading) => this.uploading.emit(uploading));
  }

  private imageUrlToUploadingListItem(url: string): UploadingListItem {
    return {
      url$: of(url),
      status$: of(UploadStatus.success),
      uploadingProgress$: of(100),
    };
  }

  private getUploadingItemsByStatus(
    targetStatus: UploadStatus | UploadStatus[],
  ): Observable<UploadingListItem[]> {
    const targetStatuses = Array.isArray(targetStatus) ? targetStatus : [targetStatus];

    return this.uploadingListItems$.pipe(
      map((items) =>
        items.map((item) =>
          item.status$.pipe(map((status) => (targetStatuses.includes(status) ? item : null))),
        ),
      ),
      switchMap((items) => iif(() => !!items.length, combineLatest(items), of([]))),
      map((items) => items.filter((item) => !!item)),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
